import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star } from "lucide-react";
import CustomerLayout, { TopBar } from "@/components/customer/CustomerLayout";
import { BannerCarousel } from "@/components/customer/BannerCarousel";
import { CategoryRail } from "@/components/customer/CategoryRail";
import { RestaurantCard } from "@/components/customer/RestaurantCard";
import { SectionHeader, Rail, SkelCard } from "@/components/common";
import { api, inr } from "@/lib/api";

export default function Home() {
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/home").then((r) => setData(r.data)).catch(() => {});
  }, []);

  return (
    <CustomerLayout>
      <TopBar />
      <div className="animate-fade-up">
        {!data ? (
          <div className="mt-4 space-y-6">
            <div className="px-4"><div className="skel h-40 rounded-2xl" /></div>
            <Rail>{[0, 1, 2].map((i) => <SkelCard key={i} />)}</Rail>
          </div>
        ) : (
          <>
            <div className="mt-4"><BannerCarousel banners={data.banners} /></div>
            <WelcomeBanner nav={nav} />

            <div className="mt-5 px-4 grid grid-cols-3 gap-3">
              {[["Food", "/", "from-coral to-coral-dark", "🍽️"], ["Quick", "/quick", "from-emerald-600 to-teal-600", "🛒"], ["Healthy", "/healthy", "from-emerald-500 to-green-700", "🥗"], ["Meal Plans", "/meals", "from-orange-500 to-rose-600", "📅"], ["Cinema", "/cinema", "from-fuchsia-700 to-purple-800", "🎬"], ["DineOut", "/dineout", "from-purple-700 to-fuchsia-600", "🍷"], ["Late Night", "/late-night", "from-indigo-800 to-slate-900", "🌙"], ["Value Eats", "/value-eats", "from-emerald-500 to-green-600", "💸"], ["Food on Train", "/train", "from-sky-600 to-blue-700", "🚆"], ["Offers", "/offers", "from-amber-500 to-orange-600", "🎁"]].map(([l, to, grad, e]) => (
                <button key={l} onClick={() => nav(to)} className={`bg-gradient-to-br ${grad} text-white rounded-2xl p-3 text-left tap`} data-testid={`service-${l.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="text-2xl">{e}</div><div className="font-bold text-sm mt-1">{l}</div>
                </button>
              ))}
            </div>

            <div className="mt-6">
              <SectionHeader title="What's on your mind?" />
              <CategoryRail categories={data.categories} />
            </div>

            {data.popular_dishes?.length > 0 && (
              <div className="mt-7">
                <SectionHeader title="Popular Dishes" />
                <Rail>
                  {data.popular_dishes.map((d) => (
                    <button key={d.id} onClick={() => nav(`/restaurant/${d.restaurant_id}`)} className="min-w-[150px] w-[150px] snap-start-m text-left tap" data-testid={`popular-${d.id}`}>
                      <img src={d.image} alt={d.name} loading="lazy" className="w-full h-28 object-cover rounded-2xl" />
                      <div className="font-bold text-sm text-ink mt-2 truncate">{d.name}</div>
                      <div className="text-xs text-ink-600 truncate">{d.restaurant_name}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-sm font-semibold">{inr(d.price)}</span>
                        <span className="text-[11px] text-ink-600 flex items-center gap-0.5"><Star size={10} className="fill-gold text-gold" />{d.rating}</span>
                      </div>
                    </button>
                  ))}
                </Rail>
              </div>
            )}

            {data.sections.map((s) => (
              <div key={s.key} className="mt-7">
                <SectionHeader title={s.title} action="See all" onAction={() => nav(`/search?cat=${encodeURIComponent(s.restaurants[0]?.cuisines?.[0] || "")}`)} />
                <Rail>
                  {s.restaurants.map((r) => <RestaurantCard key={r.id} r={r} />)}
                </Rail>
              </div>
            ))}
            <div className="text-center text-xs text-ink-600 py-8 font-medium">BiteGoFood · Food Near You, Faster Than Ever.</div>
          </>
        )}
      </div>
    </CustomerLayout>
  );
}

function WelcomeBanner({ nav }) {
  const [offer, setOffer] = useState(null);
  useEffect(() => { api.get("/customer/welcome-offer").then((r) => { if (r.data?.eligible) setOffer(r.data); }).catch(() => {}); }, []);
  if (!offer) return null;
  return (
    <div className="mx-4 mt-3 rounded-2xl bg-gradient-to-r from-coral to-orange-500 text-white p-4 flex items-center justify-between" data-testid="welcome-offer-banner">
      <div>
        <div className="font-display font-extrabold text-lg">🎉 {offer.title}</div>
        <div className="text-xs text-white/90">Use code <b>{offer.code}</b> on your first order · min ₹{offer.min_order}</div>
      </div>
      <button onClick={() => nav("/")} className="bg-white text-coral font-bold text-sm px-3 py-2 rounded-xl shrink-0" data-testid="welcome-offer-cta">Order now</button>
    </div>
  );
}

